import type { ReactNode, SVGProps } from 'react';
import type { District, TileType } from '../types/gameTypes';

/**
 * Frakto icon set — every pictogram on the board, in the lobby and in the HUD is a
 * hand-drawn SVG. No emoji anywhere in the interface, so the look stays consistent
 * on Windows, macOS, Android and iOS (emoji fonts render differently on each).
 */

export interface GameIconProps extends SVGProps<SVGSVGElement> {
  size?: number;
  /** Filled variant for badges / pawns. */
  filled?: boolean;
}

const Icon = ({ size = 20, filled = false, children, ...props }: GameIconProps & { children: ReactNode }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={filled ? 'currentColor' : 'none'}
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
    {...props}
  >
    {children}
  </svg>
);

// ---- Districts -------------------------------------------------------------

export const HouseIcon = (props: GameIconProps) => (
  <Icon {...props}>
    <path d="M3.5 10.5 12 3l8.5 7.5" />
    <path d="M5.5 9.5V21h13V9.5" />
    <path d="M9.5 21v-6h5v6" />
  </Icon>
);

export const AnchorIcon = (props: GameIconProps) => (
  <Icon {...props}>
    <circle cx="12" cy="5" r="2.4" />
    <path d="M12 7.4V21" />
    <path d="M6 13H4.5a7.5 7.5 0 0 0 15 0H18" />
    <path d="M8 10.5h8" />
  </Icon>
);

export const BulbIcon = (props: GameIconProps) => (
  <Icon {...props}>
    <path d="M9 18h6" />
    <path d="M10 21h4" />
    <path d="M8.2 14.5a6 6 0 1 1 7.6 0c-.8.7-1.3 1.5-1.4 2.5H9.6c-.1-1-.6-1.8-1.4-2.5Z" />
  </Icon>
);

export const CityIcon = (props: GameIconProps) => (
  <Icon {...props}>
    <path d="M3 21h18" />
    <path d="M4 21V9l5-3v15" />
    <path d="M9 21V11l6-4v14" />
    <path d="M15 21V13l5-3v11" />
    <path d="M6.5 11.5h.01M6.5 15h.01M11.5 13.5h.01M11.5 17h.01M17.5 15h.01" />
  </Icon>
);

export const LeafIcon = (props: GameIconProps) => (
  <Icon {...props}>
    <path d="M5 20c9 1 14-4 14-15-9 0-14 5-14 15Z" />
    <path d="M5 20c2-6 6-9 11-11" />
  </Icon>
);

export const OrbitIcon = (props: GameIconProps) => (
  <Icon {...props}>
    <circle cx="12" cy="12" r="3" />
    <ellipse cx="12" cy="12" rx="9" ry="4" transform="rotate(-25 12 12)" />
    <circle cx="19" cy="7" r="1.3" fill="currentColor" stroke="none" />
  </Icon>
);

export const TrainIcon = (props: GameIconProps) => (
  <Icon {...props}>
    <rect x="5" y="3.5" width="14" height="14" rx="4" />
    <path d="M5 10h14" />
    <path d="M9 3.5v6M15 3.5v6" />
    <path d="m7.5 17.5-2 3M16.5 17.5l2 3" />
    <circle cx="9" cy="14" r="1" fill="currentColor" stroke="none" />
    <circle cx="15" cy="14" r="1" fill="currentColor" stroke="none" />
  </Icon>
);

export const BankIcon = (props: GameIconProps) => (
  <Icon {...props}>
    <path d="M3 9.5 12 4l9 5.5" />
    <path d="M5 10v8M9.5 10v8M14.5 10v8M19 10v8" />
    <path d="M3 21h18" />
  </Icon>
);

export const StarIcon = (props: GameIconProps) => (
  <Icon {...props}>
    <path d="m12 3 2.7 5.6 6.1.9-4.4 4.3 1 6.1-5.4-2.9-5.4 2.9 1-6.1L3.2 9.5l6.1-.9L12 3Z" />
  </Icon>
);

const DISTRICT_ICONS: Record<District, (props: GameIconProps) => ReactNode> = {
  DISTRICT_A: HouseIcon,
  DISTRICT_B: AnchorIcon,
  DISTRICT_C: BulbIcon,
  DISTRICT_D: CityIcon,
  DISTRICT_E: LeafIcon,
  DISTRICT_F: OrbitIcon,
  TRANSIT: TrainIcon,
  FINANCIAL: BankIcon,
  SPECIAL: StarIcon,
};

export const DistrictIcon = ({ district, ...props }: GameIconProps & { district: District }) => {
  const Component = DISTRICT_ICONS[district] ?? StarIcon;
  return <Component {...props} />;
};

export const TileIcon = ({ tileType, district, ...props }: GameIconProps & { tileType: TileType; district: District }) => {
  if (tileType === 'COMMERCIAL') return <DistrictIcon district={district} {...props} />;
  if (tileType === 'TRANSIT') return <TrainIcon {...props} />;
  if (tileType === 'FINANCIAL') return <BankIcon {...props} />;
  return <StarIcon {...props} />;
};

// ---- Money & finance -------------------------------------------------------

export const CoinIcon = (props: GameIconProps) => (
  <Icon {...props}>
    <circle cx="12" cy="12" r="8" />
    <path d="M14.5 8.8c-.6-.6-1.5-1-2.6-1-1.6 0-2.8.9-2.8 2.1 0 1.3 1.1 1.8 2.8 2.2 1.9.4 3 1 3 2.3 0 1.3-1.3 2.2-3 2.2-1.2 0-2.2-.4-2.9-1.1" />
    <path d="M12 6.2v11.6" />
  </Icon>
);

export const CoinsIcon = (props: GameIconProps) => (
  <Icon {...props}>
    <ellipse cx="12" cy="6.5" rx="7" ry="3" />
    <path d="M5 6.5v5c0 1.7 3.1 3 7 3s7-1.3 7-3v-5" />
    <path d="M5 11.5v5c0 1.7 3.1 3 7 3s7-1.3 7-3v-5" />
  </Icon>
);

export const LoanIcon = (props: GameIconProps) => (
  <Icon {...props}>
    <rect x="3" y="6" width="18" height="12" rx="2.5" />
    <circle cx="12" cy="12" r="2.5" />
    <path d="M6.5 9.5h.01M17.5 14.5h.01" />
  </Icon>
);

export const FundIcon = (props: GameIconProps) => (
  <Icon {...props}>
    <path d="M4 19V5" />
    <path d="M4 19h16" />
    <path d="m7 15 3.5-4 3 2.5L20 6" />
    <path d="M20 10V6h-4" />
  </Icon>
);

export const ShieldIcon = (props: GameIconProps) => (
  <Icon {...props}>
    <path d="M12 21s7.5-3.7 7.5-9.5V5.5L12 3 4.5 5.5v6c0 5.8 7.5 9.5 7.5 9.5Z" />
    <path d="m9 11.8 2 2 4-4" />
  </Icon>
);

export const WalletIcon = (props: GameIconProps) => (
  <Icon {...props}>
    <rect x="3" y="6" width="18" height="14" rx="3" />
    <path d="M3 10h18" />
    <path d="M16.5 15h1.5" />
    <path d="M6 6V4.5A1.5 1.5 0 0 1 7.5 3H17" />
  </Icon>
);

export const CrownIcon = (props: GameIconProps) => (
  <Icon {...props}>
    <path d="m3 8 4 3 5-6 5 6 4-3-2 11H5L3 8Z" />
    <path d="M5 19h14" />
  </Icon>
);

export const GavelIcon = (props: GameIconProps) => (
  <Icon {...props}>
    <path d="m4 20 7-7" />
    <path d="m9.5 8.5 6 6" />
    <path d="m12 3 6 6-2.5 2.5-6-6L12 3Z" />
    <path d="m6.5 12.5 5 5" />
  </Icon>
);

export const ReceiptIcon = (props: GameIconProps) => (
  <Icon {...props}>
    <path d="M6 3h12v18l-2-1.5-2 1.5-2-1.5-2 1.5-2-1.5L6 21V3Z" />
    <path d="M9 8h6M9 12h6M9 16h3" />
  </Icon>
);

export const TrophyIcon = (props: GameIconProps) => (
  <Icon {...props}>
    <path d="M8 4h8v5a4 4 0 0 1-8 0V4Z" />
    <path d="M8 6H5v1.5A3.5 3.5 0 0 0 8.5 11M16 6h3v1.5A3.5 3.5 0 0 1 15.5 11" />
    <path d="M12 13v4M8.5 21h7M10 21c0-2 1-3 2-3s2 1 2 3" />
  </Icon>
);

export const TradeIcon = (props: GameIconProps) => (
  <Icon {...props}>
    <path d="M4 8h13" />
    <path d="m14 5 3 3-3 3" />
    <path d="M20 16H7" />
    <path d="m10 13-3 3 3 3" />
  </Icon>
);

export const MetroIcon = (props: GameIconProps) => (
  <Icon {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M7 15V9l5 4 5-4v6" />
  </Icon>
);

export const FactoryIcon = (props: GameIconProps) => (
  <Icon {...props}>
    <path d="M3 21h18V9l-5 3V9l-5 3V9l-5 3v9Z" />
    <path d="M7 17h2M12 17h2M17 17h2" />
    <path d="M6 9V4h3v5" />
  </Icon>
);

// ---- Game actions ----------------------------------------------------------

export const DiceIcon = (props: GameIconProps) => (
  <Icon {...props}>
    <rect x="3" y="3" width="18" height="18" rx="4" />
    <circle cx="8.5" cy="8.5" r="1" fill="currentColor" stroke="none" />
    <circle cx="15.5" cy="8.5" r="1" fill="currentColor" stroke="none" />
    <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
    <circle cx="8.5" cy="15.5" r="1" fill="currentColor" stroke="none" />
    <circle cx="15.5" cy="15.5" r="1" fill="currentColor" stroke="none" />
  </Icon>
);

export const FlagIcon = (props: GameIconProps) => (
  <Icon {...props}>
    <path d="M5 21V4" />
    <path d="M5 5h10l-1.5 3L15 11H5" />
  </Icon>
);

export const BotIcon = (props: GameIconProps) => (
  <Icon {...props}>
    <rect x="4" y="7" width="16" height="12" rx="3" />
    <path d="M12 3v4" />
    <circle cx="9" cy="13" r="1.2" fill="currentColor" stroke="none" />
    <circle cx="15" cy="13" r="1.2" fill="currentColor" stroke="none" />
    <path d="M9 16.5h6" />
    <path d="M2 11v4M22 11v4" />
  </Icon>
);

export const UserIcon = (props: GameIconProps) => (
  <Icon {...props}>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21c0-4 3.6-6 8-6s8 2 8 6" />
  </Icon>
);

export const UsersIcon = (props: GameIconProps) => (
  <Icon {...props}>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </Icon>
);

export const ChartIcon = (props: GameIconProps) => (
  <Icon {...props}>
    <path d="M4 20V4" />
    <path d="M4 20h16" />
    <rect x="7" y="12" width="3" height="5" rx="1" />
    <rect x="12" y="9" width="3" height="8" rx="1" />
    <rect x="17" y="6" width="3" height="11" rx="1" />
  </Icon>
);

export const LedgerIcon = (props: GameIconProps) => (
  <Icon {...props}>
    <rect x="4" y="3" width="16" height="18" rx="2.5" />
    <path d="M8 7h8M8 11h8M8 15h5" />
  </Icon>
);

export const MenuIcon = (props: GameIconProps) => (
  <Icon {...props}>
    <path d="M4 7h16M4 12h16M4 17h16" />
  </Icon>
);

export const PlayIcon = (props: GameIconProps) => (
  <Icon {...props}>
    <path d="M7 4.5v15l12-7.5-12-7.5Z" />
  </Icon>
);

export const PauseIcon = (props: GameIconProps) => (
  <Icon {...props}>
    <path d="M8 4v16M16 4v16" />
  </Icon>
);

export const SaveIcon = (props: GameIconProps) => (
  <Icon {...props}>
    <path d="M5 3h11l3 3v15H5V3Z" />
    <path d="M8 3v6h7V3" />
    <rect x="8" y="13" width="8" height="8" rx="1" />
  </Icon>
);

export const SpeedIcon = (props: GameIconProps) => (
  <Icon {...props}>
    <path d="M4 18a8 8 0 1 1 16 0" />
    <path d="m12 14 4-4" />
    <circle cx="12" cy="14" r="1.2" fill="currentColor" stroke="none" />
  </Icon>
);

export const HeatmapIcon = (props: GameIconProps) => (
  <Icon {...props}>
    <rect x="3" y="3" width="7" height="7" rx="1.5" />
    <rect x="14" y="3" width="7" height="7" rx="1.5" />
    <rect x="3" y="14" width="7" height="7" rx="1.5" />
    <rect x="14" y="14" width="7" height="7" rx="1.5" fill="currentColor" />
  </Icon>
);

export const SnowIcon = (props: GameIconProps) => (
  <Icon {...props}>
    <path d="M12 3v18M4.2 7.5l15.6 9M19.8 7.5l-15.6 9" />
    <path d="m9 5 3 2 3-2M9 19l3-2 3 2" />
  </Icon>
);

export const PlusIcon = (props: GameIconProps) => (
  <Icon {...props}>
    <path d="M12 5v14M5 12h14" />
  </Icon>
);

export const MinusIcon = (props: GameIconProps) => (
  <Icon {...props}>
    <path d="M5 12h14" />
  </Icon>
);

export const CloseIcon = (props: GameIconProps) => (
  <Icon {...props}>
    <path d="M18 6 6 18M6 6l12 12" />
  </Icon>
);

export const CheckIcon = (props: GameIconProps) => (
  <Icon {...props}>
    <path d="m20 6-11 11-5-5" />
  </Icon>
);

export const SparkIcon = (props: GameIconProps) => (
  <Icon {...props}>
    <path d="m12 3 1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3Z" />
  </Icon>
);

export const ArrowRightIcon = (props: GameIconProps) => (
  <Icon {...props}>
    <path d="M4 12h16" />
    <path d="m14 6 6 6-6 6" />
  </Icon>
);

export const HomeIcon = (props: GameIconProps) => (
  <Icon {...props}>
    <path d="M3 11 12 3l9 8" />
    <path d="M5.5 9.5V21h13V9.5" />
  </Icon>
);

export const InfoIcon = (props: GameIconProps) => (
  <Icon {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5" />
    <circle cx="12" cy="7.5" r="1" fill="currentColor" stroke="none" />
  </Icon>
);

export const LockIcon = (props: GameIconProps) => (
  <Icon {...props}>
    <rect x="4.5" y="10" width="15" height="11" rx="2.5" />
    <path d="M8 10V7a4 4 0 0 1 8 0v3" />
  </Icon>
);

export const CloudIcon = (props: GameIconProps) => (
  <Icon {...props}>
    <path d="M7 18h10a4 4 0 0 0 .8-7.9A6 6 0 0 0 6.2 9 4.5 4.5 0 0 0 7 18Z" />
  </Icon>
);
